import { Euler, Vector3, type PerspectiveCamera } from 'three';

/**
 * A way to actually look at the building.
 *
 * There is no player yet, so until the character controller lands the only way
 * into this map is a set of fixed cameras driven from the browser console. That
 * is fine for the screenshot harness and useless for anyone who just wants to
 * see the place. This is the review tool: the authored shots on hotkeys, plus a
 * free-fly camera for everything between them.
 *
 * It is explicitly NOT the game's camera and should not grow into it. It has no
 * collision, no gravity, no head bob and no eye height — it flies through walls
 * on purpose, because the job here is inspecting geometry, not playing. When the
 * real controller arrives this stays as a debug view and the two do not share
 * code; a review camera that has to obey collision is a review camera that can
 * no longer get you to the one place you need to look at.
 */

/** Metres per second. Walking pace, so distances read honestly while touring. */
const SPEED = 5.5;
/** Held-shift multiplier. The mall is 110m end to end; crossing it should not take a minute. */
const BOOST = 4;
const LOOK = 0.0022;

export interface ViewerDeps {
  camera: PerspectiveCamera;
  /** The canvas, for pointer-lock. Clicking the overlay must not grab the mouse. */
  surface: HTMLCanvasElement;
  /** Fixed camera names, in the order they should be offered. */
  shots: readonly string[];
  /** Jump to a fixed camera. Owned by main so it can re-apply zone fog. */
  goTo(name: string): void;
}

export interface Viewer {
  /** Advance free-fly movement. Call once per frame from the render loop. */
  update(dt: number): void;
  /** Hide the overlay. The screenshot harness captures the whole page, not the canvas. */
  setChromeVisible(visible: boolean): void;
  /**
   * Take the camera, mouse and keyboard, or give them back.
   *
   * Once there is a player, two things want pointer-lock and both want WASD. The
   * review tool is the one that yields: it goes completely inert — no listeners
   * acting, no overlay, no free-fly — so that in play mode this file may as well
   * not be loaded.
   */
  setEnabled(on: boolean): void;
  readonly enabled: boolean;
}

export function installViewer(deps: ViewerDeps): Viewer {
  const { camera, surface, shots } = deps;

  const held = new Set<string>();
  const euler = new Euler(0, 0, 0, 'YXZ');
  let flying = false;
  let enabled = true;
  let current: string | null = shots[0] ?? null;

  // ---------------------------------------------------------------------------
  // Overlay
  // ---------------------------------------------------------------------------

  const root = document.createElement('div');
  root.id = 'viewer-chrome';
  root.innerHTML = `
    <style>
      #viewer-chrome {
        position: fixed; inset: 0; pointer-events: none;
        font: 11px/1.5 ui-monospace, "SF Mono", Menlo, monospace;
        letter-spacing: 0.04em; color: #9fb0c4;
        transition: opacity 140ms ease;
      }
      #viewer-chrome .panel {
        position: absolute; left: 16px; top: 16px; pointer-events: auto;
        background: rgba(8, 11, 16, 0.72); border: 1px solid rgba(150, 175, 205, 0.16);
        backdrop-filter: blur(9px); padding: 10px 0 8px; min-width: 178px;
      }
      #viewer-chrome h1 {
        font-size: 10px; font-weight: 600; letter-spacing: 0.22em; color: #6d7f94;
        padding: 0 12px 8px; margin: 0;
      }
      #viewer-chrome button {
        display: flex; justify-content: space-between; gap: 12px; width: 100%;
        background: none; border: 0; cursor: pointer; text-align: left;
        font: inherit; color: #9fb0c4; padding: 3px 12px;
      }
      #viewer-chrome button:hover { background: rgba(150, 175, 205, 0.09); color: #dce6f2; }
      #viewer-chrome button[data-on="1"] { background: rgba(120, 170, 235, 0.16); color: #eaf2ff; }
      #viewer-chrome button i { font-style: normal; color: #55677c; }
      #viewer-chrome .hint {
        position: absolute; left: 16px; bottom: 14px; max-width: 460px;
        color: #5f7186; text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
      }
      #viewer-chrome .hint b { color: #9fb0c4; font-weight: 500; }
    </style>
    <div class="panel"><h1>VIEWPOINTS</h1><div class="list"></div></div>
    <div class="hint"></div>
  `;
  document.body.appendChild(root);

  const list = root.querySelector('.list') as HTMLElement;
  const hint = root.querySelector('.hint') as HTMLElement;
  const buttons = new Map<string, HTMLButtonElement>();

  shots.forEach((name, i) => {
    const b = document.createElement('button');
    // Only the first ten get a digit. Beyond that the bracket keys and the
    // mouse are the way through, which is why the list is clickable at all.
    const key = i < 9 ? String(i + 1) : i === 9 ? '0' : '';
    b.innerHTML = `<span>${name}</span><i>${key}</i>`;
    b.onclick = () => jump(name);
    list.appendChild(b);
    buttons.set(name, b);
  });

  function paint() {
    for (const [name, b] of buttons) b.dataset.on = name === current && !flying ? '1' : '0';
    hint.innerHTML = flying
      ? '<b>WASD</b> move &nbsp; <b>mouse</b> look &nbsp; <b>shift</b> sprint &nbsp; ' +
        '<b>space/ctrl</b> up/down &nbsp; <b>esc</b> release mouse'
      : '<b>click</b> to fly freely &nbsp; <b>1-0</b> jump to a viewpoint &nbsp; ' +
        '<b>[ ]</b> previous/next &nbsp; <b>h</b> hide this';
  }

  // ---------------------------------------------------------------------------
  // Camera control
  // ---------------------------------------------------------------------------

  function jump(name: string) {
    current = name;
    deps.goTo(name);
    // Re-derive yaw and pitch from wherever the shot left us pointing, so
    // stepping into free-fly afterwards continues the view instead of snapping
    // back to whatever direction the mouse was last dragged in.
    euler.setFromQuaternion(camera.quaternion);
    if (document.pointerLockElement) document.exitPointerLock();
    paint();
  }

  function cycle(step: number) {
    const i = current ? shots.indexOf(current) : -1;
    jump(shots[(i + step + shots.length) % shots.length]);
  }

  surface.addEventListener('click', () => enabled && surface.requestPointerLock());
  document.addEventListener('pointerlockchange', () => {
    flying = enabled && document.pointerLockElement === surface;
    // Keys held at the moment the mouse is released would otherwise stay held
    // forever — the keyup lands on a document that is no longer listening for it.
    if (!flying) held.clear();
    paint();
  });

  document.addEventListener('mousemove', (e) => {
    if (!flying) return;
    euler.y -= e.movementX * LOOK;
    euler.x -= e.movementY * LOOK;
    // Stop just short of straight up or down. Passing vertical flips the
    // horizon and there is no recovering the roll in a YXZ euler.
    euler.x = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, euler.x));
    camera.quaternion.setFromEuler(euler);
    current = null;
  });

  document.addEventListener('keydown', (e) => {
    if (!enabled) return;
    const digit = e.key === '0' ? 9 : '123456789'.indexOf(e.key);
    if (digit >= 0 && digit < shots.length) return jump(shots[digit]);
    if (e.key === '[') return cycle(-1);
    if (e.key === ']') return cycle(1);
    if (e.key === 'h') return void (root.style.opacity = root.style.opacity === '0' ? '1' : '0');
    held.add(e.code);
  });
  document.addEventListener('keyup', (e) => held.delete(e.code));
  // A tab-away with W down leaves the camera flying off into the fog forever.
  window.addEventListener('blur', () => held.clear());

  const forward = new Vector3();
  const right = new Vector3();
  const step = new Vector3();

  paint();

  return {
    update(dt: number) {
      if (!enabled || !flying || !held.size) return;
      camera.getWorldDirection(forward);
      right.crossVectors(forward, camera.up).normalize();

      step.set(0, 0, 0);
      if (held.has('KeyW')) step.add(forward);
      if (held.has('KeyS')) step.sub(forward);
      if (held.has('KeyD')) step.add(right);
      if (held.has('KeyA')) step.sub(right);
      if (held.has('Space')) step.y += 1;
      if (held.has('ControlLeft') || held.has('KeyC')) step.y -= 1;
      if (!step.lengthSq()) return;

      const boost = held.has('ShiftLeft') || held.has('ShiftRight') ? BOOST : 1;
      camera.position.addScaledVector(step.normalize(), SPEED * boost * dt);
      current = null;
    },
    setChromeVisible(visible: boolean) {
      root.style.display = visible ? '' : 'none';
    },
    setEnabled(on: boolean) {
      if (on === enabled) return;
      enabled = on;
      root.style.display = on ? '' : 'none';
      if (!on) {
        held.clear();
        flying = false;
        if (document.pointerLockElement === surface) document.exitPointerLock();
      }
      paint();
    },
    get enabled() {
      return enabled;
    },
  };
}
